import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import HistoryIcon from '@mui/icons-material/History'
import InfoIcon from '@mui/icons-material/Info'
import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Modal from '@mui/material/Modal'
import Typography from '@mui/material/Typography'
import moment from 'moment'
import { MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Button } from '~/components/Atoms/Button'
import { IconBox } from '~/components/Atoms/IconBox'
import { DownloadContext } from '~/components/Molecules/DownloadContext'
import { NAV_LINKS } from '~/constants'

import { useCompany } from '~/lib/company'
import { useStats } from '~/lib/stats'

export function ReportCard() {
  const theme = useTheme()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data } = useStats()
  const { data: companyData } = useCompany()
  const [open, setOpen] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  return (
    <Box
      display="flex"
      flexDirection="column"
      padding={theme.spacing.XXL}
      borderRadius={theme.radius.M}
      gap={theme.spacing['4XL']}
      bgcolor={theme.palette.common.white}
    >
      <Box display="flex" gap={theme.spacing.L} width="100%">
        <IconBox icon={<InfoIcon />} />
        <Box display="flex" flexDirection="column" width="100%" alignSelf="flex-end">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body">{t('home:report.title')}</Typography>
            <Box
              onClick={() => {
                setOpenModal(true)
              }}
              display="flex"
              sx={{ cursor: 'pointer' }}
            >
              <Typography variant="body-small" sx={{ textDecoration: 'underline' }}>
                {t('home:report.more')}
              </Typography>
            </Box>
          </Box>
          <Typography variant="body-small" color={theme.palette.text.mainLight}>
            {t('home:report.subtitle')}
          </Typography>
        </Box>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        borderRadius={theme.radius.M}
        padding={`${theme.spacing['3XL']} ${theme.spacing.L}`}
        bgcolor={theme.palette.primary.lightest}
      >
        <Box display="flex" flexDirection="column" gap={theme.spacing.S}>
          <Box display="flex" gap={theme.spacing.L}>
            <Typography variant="body-bold">{t('home:report.historyTitle')}</Typography>
          </Box>
          <Typography height="66px" variant="body-small" color={theme.palette.text.mainLight}>
            {t('home:report.content', { company: companyData?.name })}
          </Typography>
        </Box>
        <Box display="flex" flexDirection="row-reverse">
          <Button
            onClick={() => navigate(NAV_LINKS.REPORT.to)}
            label={t('home:report.history')}
            variant="contained"
            endIcon={<HistoryIcon />}
          />
        </Box>
      </Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body-small" color={theme.palette.text.mainLight}>
          {data && `${t('home:report.recent')} : ${moment(data[0]?.publication_date).format('DD/MM/YYYY')}`}
        </Typography>
        <Button
          disabled={!(data && data.length >= 1)}
          onClick={(event: MouseEvent<HTMLButtonElement>) => {
            setOpen(true)
            setAnchorEl(event.currentTarget)
          }}
          label={t('home:report.downloadLast')}
          endIcon={<DownloadOutlinedIcon />}
        />
      </Box>
      <DownloadContext anchorEl={anchorEl} id={(data && data[0]?.id) ?? ''} open={open} setOpen={setOpen} />
      <Modal
        open={openModal}
        onClose={() => {
          setOpenModal(false)
        }}
        sx={{ outline: 0 }}
      >
        <Box
          display="flex"
          flexDirection="column"
          borderRadius={theme.radius.L}
          bgcolor={theme.palette.common.white}
          sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
          overflow="hidden"
        >
          <Box display="flex" padding={theme.spacing.L} bgcolor={theme.palette.primary.main}>
            <Typography variant="body-bold" color={theme.palette.common.white}>
              {t('home:report.modal.title')}
            </Typography>
          </Box>
          <Box display="flex" padding={`${theme.spacing.XXL} ${theme.spacing.L}`}>
            <ul style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.XXL }}>
              <li>
                <Typography variant="body">{t('home:report.modal.1')}</Typography>
              </li>
              <li>
                <Typography variant="body">{t('home:report.modal.2')}</Typography>
              </li>
              <li>
                <Typography variant="body">{t('home:report.modal.3')}</Typography>
                <ul
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    listStyleType: 'none',
                    marginTop: theme.spacing.XXL,
                  }}
                >
                  <li>
                    <Typography variant="body">{t('home:report.modal.4')}</Typography>
                  </li>
                  <li>
                    <Typography variant="body">{t('home:report.modal.5')}</Typography>
                  </li>
                  <li>
                    <Typography variant="body">{t('home:report.modal.6')}</Typography>
                  </li>
                </ul>
              </li>
              <li>
                <Typography variant="body">{t('home:report.modal.7')}</Typography>
              </li>
              <li>
                <Typography variant="body">{t('home:report.modal.8')}</Typography>
              </li>
              <li>
                <Typography variant="body">{t('home:report.modal.9')}</Typography>
              </li>
              <li>
                <Typography variant="body">{t('home:report.modal.10')}</Typography>
              </li>
            </ul>
          </Box>
          <Box display="flex" flexDirection="row-reverse" padding={`${theme.spacing.M} ${theme.spacing.L}`}>
            <Button
              variant="outlined"
              label="Fermer"
              onClick={() => {
                setOpenModal(false)
              }}
            />
          </Box>
        </Box>
      </Modal>
    </Box>
  )
}
